import { Checkbox, Select } from "antd";
import _ from "lodash";
import React, { useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Button } from "@components/Button";
import ErrorValidate from "@components/ErrorValidate";
import { StyledInput } from "@components/Input";
import { Text } from "@components/Text";
import { COLOR_DARK, COLOR_GREEN, COLOR_WHITE } from "@static/COLORS";
import { fetchAPI } from "@utils/https/fetchAPI";
import { VALIDATE_RULES } from "@validate/index";

const FormContainer = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 2px;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const GroupInput = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const FormCreate = ({
  setDataList = () => undefined,
  setIsModalVisible,
  roleOptions = [],
  customerOptions = [],
  dataList,
  userProfile,
}) => {
  console.log("customerOptions :>> ", customerOptions);
  const user_type = _.get(userProfile, ["user_type"]);
  const customerId = _.get(userProfile, ["category_customer_id"]);
  const [isHighestLevel, setIsHighestLevel] = useState(true);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      category_customer_id: customerId,
    },
  });

  const selectedCustomer = useWatch({
    control,
    name: "category_customer_id",
  });

  const onHandleCreate = async (data) => {
    const username = _.get(data, ["username"]);
    const password = _.get(data, ["password"]);
    const email = _.get(data, ["email"]);
    const role_id = _.get(data, ["role_id"]);
    const category_customer_id = _.get(data, ["category_customer_id"], null);
    const user_type = _.isNil(category_customer_id) ? "OWNER" : "CUSTOMER";

    // const [isNoEnd, setIsNoEnd] = useState(false);
    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/user-create",
      isAuth: true,
      data: {
        username,
        password,
        email,
        role_id,
        user_type,
        category_customer_id,
      },
    });

    if (_.get(resp, ["status"])) {
      const results = _.chain(resp).get(["result"]).value();
      toast.success("Create User Success.");
      setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
      const updateData = _.chain(dataList)
        .thru((list) => {
          return [results, ...list];
        })
        .map((item, index) => {
          return { ...item, key: index + 1, index: index + 1 };
        })
        .value();
      setDataList(updateData);
    }
  };

  const filteredRoleOptions = _.filter(roleOptions, (role) => {
    return _.get(role, ["category_customer_id"]) === null;
  });

  const onHandleCustomerChange = (value) => {
    setValue("role_id", null); // Reset location_id
    setValue("category_customer_id", value);
  };

  const onHandleHighestChange = (checked) => {
    setIsHighestLevel(checked);
    if (checked) {
      setValue("category_customer_id", null);
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onHandleCreate)} autoComplete="off">
      <Line />
      <InnerContainer>
        <div>
          <Controller
            name="username"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Username
                </Text>
                <StyledInput
                  label="username"
                  {...field}
                  size="large"
                  placeholder="username"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Password
                </Text>
                <StyledInput
                  label="password"
                  {...field}
                  size="large"
                  placeholder="password"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={_.get(VALIDATE_RULES, ["email"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Email
                </Text>
                <StyledInput
                  label="email"
                  {...field}
                  size="large"
                  placeholder="email"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />
          {user_type !== "CUSTOMER" && <></>}

          {user_type !== "CUSTOMER" && (
            <GroupInput>
              <Controller
                name="category_customer_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Text
                      color={COLOR_DARK}
                      fontSize="14px"
                      style={{ marginBottom: 4, marginLeft: 4 }}
                    >
                      Customer
                    </Text>
                    <Select
                      disabled={isHighestLevel}
                      allowClear
                      {...field}
                      size="large"
                      showSearch
                      placeholder="Select a customer"
                      optionFilterProp="children"
                      style={{ width: "100%" }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").includes(input)
                      }
                      filterSort={(optionA, optionB) =>
                        (optionA?.label ?? "")
                          .toLowerCase()
                          .localeCompare((optionB?.label ?? "").toLowerCase())
                      }
                      options={customerOptions}
                      onChange={(value) => {
                        onHandleCustomerChange(value);
                        field.onChange(value);
                      }}
                      onClear={() => {
                        setValue("category_customer_id", null);
                        field.onChange(null);
                      }}
                    />
                    <ErrorValidate justify="start" error={error} />
                  </div>
                )}
              />
              <CheckboxContainer>
                <Text color={COLOR_DARK} fontSize="14px">
                  Get LED
                </Text>
                <Checkbox
                  style={{ marginTop: 10 }}
                  checked={isHighestLevel}
                  onChange={(e) => onHandleHighestChange(e.target.checked)}
                />
              </CheckboxContainer>
            </GroupInput>
          )}

          <Controller
            name="role_id"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Role
                </Text>
                <Select
                  {...field}
                  size="large"
                  showSearch
                  placeholder="Select a role"
                  optionFilterProp="children"
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").includes(input)
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={filteredRoleOptions}
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />
        </div>

        <ButtonContainer>
          <Button
            type="submit"
            color={COLOR_GREEN}
            fontColor={COLOR_WHITE}
            fontSize="16px"
            padding="5px 20px"
          >
            Save
          </Button>
        </ButtonContainer>
      </InnerContainer>
    </FormContainer>
  );
};
